import BaseApiService from "./BaseApiService";

/**
 * Authentication API Service
 *
 * Handles all authentication-related API calls including:
 * - User login/logout
 * - User registration
 * - Password reset
 * - Email verification
 * - Token refresh
 *
 * @class AuthApiService
 * @extends BaseApiService
 */
class AuthApiService extends BaseApiService {
  /**
   * Authenticates user with email and password
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @returns {Promise<Object>} Authentication response with tokens and user info
   */
  async authenticate(email, password) {
    return await this.post("/auth/authenticate", { email, password });
  }

  /**
   * Gets user authentication flags (for page refresh)
   * @returns {Promise<Object>} User flags and data
   */
  async getAuthFlags() {
    return await this.get("/auth/flags");
  }

  /**
   * Registers a new user
   * @param {Object} userData - User registration data
   * @param {string} userData.email - User's email
   * @param {string} userData.password - User's password
   * @param {string} userData.name - User's full name
   * @param {string} [userData.language] - Preferred language
   * @returns {Promise<Object>} Registration response
   */
  async register(userData) {
    return await this.post("/auth/register", userData);
  }

  /**
   * Registers a new physiotherapist
   * @param {Object} physioData - Physiotherapist registration data
   * @param {string} physioData.email - Physio's email
   * @param {string} physioData.password - Physio's password
   * @param {string} physioData.name - Physio's full name
   * @param {string} physioData.clinicName - Clinic name
   * @param {string} [physioData.language] - Preferred language
   * @returns {Promise<Object>} Registration response
   */
  async registerPhysio(physioData) {
    return await this.post("/auth/register/physio", physioData);
  }

  /**
   * Sends password reset email
   * @param {string} email - User's email address
   * @returns {Promise<string>} Reset email status
   */
  async sendPasswordReset(email) {
    return await this.post("/auth/send/reset/password", { email });
  }

  /**
   * Resets password using reset token
   * @param {string} token - Password reset token
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Reset confirmation
   */
  async resetPassword(token, newPassword) {
    return await this.post("/auth/reset/password", {
      token,
      newPassword,
    });
  }

  /**
   * Verifies user's email address
   * @param {string} verificationToken - Email verification token
   * @returns {Promise<Object>} Verification response
   */
  async verifyEmail(verificationToken) {
    return await this.post("/auth/verify/email", {
      token: verificationToken,
    });
  }

  /**
   * Resends email verification
   * @param {string} email - User's email address
   * @returns {Promise<Object>} Resend confirmation
   */
  async resendEmailVerification(email) {
    return await this.post("/auth/resend/verification", { email });
  }

  /**
   * Refreshes access token (overrides base method for specific endpoint)
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<{token: string, refreshToken: string}>} New tokens
   */
  async refreshToken(refreshToken) {
    // Use the base class method but with explicit endpoint
    return await super.refreshAccessToken(refreshToken);
  }

  /**
   * Validates authentication status
   * @returns {Promise<Object>} User authentication status
   */
  async validateToken() {
    return await this.get("/auth/validate");
  }

  /**
   * Logs out user (if backend supports logout endpoint)
   * @returns {Promise<Object>} Logout confirmation
   */
  async logout() {
    try {
      return await this.post("/auth/logout");
    } catch (error) {
      // If logout endpoint doesn't exist, just clear local tokens
      return { success: true, message: "Logged out locally" };
    }
  }

  /**
   * Changes user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Password change confirmation
   */
  async changePassword(currentPassword, newPassword) {
    return await this.post("/auth/change/password", {
      currentPassword,
      newPassword,
    });
  }

  /**
   * Gets user profile information
   * @returns {Promise<Object>} User profile data
   */
  async getUserProfile() {
    return await this.get("/auth/profile");
  }

  /**
   * Updates user profile information
   * @param {Object} profileData - Updated profile data
   * @returns {Promise<Object>} Update confirmation
   */
  async updateUserProfile(profileData) {
    return await this.put("/auth/profile", profileData);
  }

  /**
   * Changes user's current product
   * @param {string} selectedProduct - Selected product type ('corporate', 'preventive_course', 'physio')
   * @returns {Promise<Object>} Product change confirmation with updated auth info
   */
  async changeProduct(selectedProduct) {
    const response = await this.post("/auth/changeProduct", {
      current_product: selectedProduct,
    }, { withCredentials: true});

    return response;
  }

  /**
   * Mark user's onboarding as complete (based on current_product)
   * @returns {Promise<Object>} User's onboarding completion status (HTTP STATUS)
   */
  async completeOnboarding() {
    return await this.post("/auth/complete-onboarding")
  }

  /**
   * Update new user's password after logging in the first time
   * @param {Object} updateObject - Update object containing user details
   * @returns {Promise<Object>} 200 OK with update or 401 unauthorized
   */
  async updateNewUserPassword(updateObject) {
    return await this.post("/auth/update/new-user-password", updateObject);
  }
}

// Create and export a singleton instance
const authApiService = new AuthApiService();
export default authApiService;
